import type { LanguageType } from '@repo/sdk'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'

export type TournamentEdition = NonNullable<
  inferRouterOutputs<AppRouter>['tournamentRouter']['getEdition']
>

export const tournamentPhaseKeys = {
  UNSCHEDULED: 'placeholderState',
  UPCOMING_REGISTRATION: 'upcoming',
  REGISTRATION_OPEN: 'registrationOpen',
  WAITING_FOR_COMPETITION: 'waiting',
  COMPETITION_OPEN: 'competitionOpen',
  ENDED: 'ended',
} as const

export function tournamentUrl(editionId?: string, view?: 'rules') {
  const path = editionId
    ? `/tournaments/${encodeURIComponent(editionId)}`
    : '/tournaments'
  return view ? `${path}?view=${view}` : path
}

export function tournamentDateRange(
  start: string,
  end: string,
  timezone: string,
  language: string,
) {
  const format = new Intl.DateTimeFormat(language, {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return `${format.format(new Date(start))} — ${format.format(new Date(Date.parse(end) - 1))}`
}

export function useTournamentEdition(editionId?: string) {
  const { language } = useI18n()
  const { actor, authResolved } = useAuth()
  const trpc = useTRPC()
  const editionQuery = useQuery({
    ...trpc.tournamentRouter.getEdition.queryOptions({
      editionId,
      language: language as LanguageType,
    }),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })
  const edition = editionQuery.data
  const viewerOptions = trpc.tournamentRouter.getViewer.queryOptions({
    editionId: edition?.id ?? '',
  })
  const viewerContext = {
    ...viewerOptions.queryKey[1],
    actorId: actor?.id ?? null,
  }
  const viewerQuery = useQuery({
    ...viewerOptions,
    queryKey: [
      viewerOptions.queryKey[0],
      viewerContext,
    ],
    enabled: !!edition && authResolved,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  })
  const { refetch: refetchEdition } = editionQuery
  const { refetch: refetchViewer } = viewerQuery
  const lifecycle = viewerQuery.data?.lifecycle ?? edition?.lifecycle
  useEffect(() => {
    if (!lifecycle?.nextTransitionAt)
      return
    const remaining
      = Date.parse(lifecycle.nextTransitionAt) - Date.parse(lifecycle.serverNow)
    const timer = window.setTimeout(
      () => {
        void refetchEdition()
        void refetchViewer()
      },
      Math.min(Math.max(remaining + 100, 100), 2_147_483_647),
    )
    return () => window.clearTimeout(timer)
  }, [lifecycle, refetchEdition, refetchViewer])
  return {
    edition,
    editionQuery,
    viewer: viewerQuery.data,
    viewerQuery,
    lifecycle,
  }
}
